import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Minus, Plus, Loader2, ShoppingBag, Download, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import api, { mediaUrl } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const CartPage = () => {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState(user?.full_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [loading, setLoading] = useState(false);
  const hasPhysical = items.some((i) => i.product_type === 'physical');

  const checkout = async () => {
    if (items.length === 0) return;
    if (!email) return toast({ title: 'Please enter your email for the receipt & downloads' });
    setLoading(true);
    try {
      const res = await api.post('/shop/checkout', {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        name, email, origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast({ title: 'Checkout failed', description: e?.response?.data?.detail || 'Please try again.' });
      setLoading(false);
    }
  };

  return (
    <div className="App" data-testid="page-cart">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
          <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy mb-8">Your <span className="font-italic-display text-rose-italic">Cart</span></h1>

          {items.length === 0 ? (
            <div className="text-center py-16" data-testid="cart-empty">
              <ShoppingBag className="w-10 h-10 text-mahogany/40 mx-auto mb-4" />
              <p className="text-mahogany/70">Your cart is empty.</p>
              <Link to="/shop" className="pill-btn mt-5 inline-flex">Browse the Store</Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-start">
              <ul className="space-y-4" data-testid="cart-items">
                {items.map((i) => (
                  <li key={i.product_id} className="flex gap-4 p-4 rounded-2xl bg-white/70 border border-rose/40" data-testid={`cart-item-${i.slug}`}>
                    <SmartImage src={mediaUrl(i.image)} alt={i.title} className="w-20 h-24 rounded-lg shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Link to={`/product/${i.slug}`} className="font-serif-display text-xl text-burgundy hover:text-rose-italic">{i.title}</Link>
                          {i.product_type === 'digital' && <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-mahogany text-cream inline-flex items-center gap-1"><Download className="w-2.5 h-2.5" /> Digital</span>}
                          <div className="text-mahogany text-sm mt-1">${i.price.toFixed(2)}</div>
                        </div>
                        <button onClick={() => removeItem(i.product_id)} className="text-red-500" data-testid={`cart-remove-${i.slug}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <div className="mt-3 inline-flex items-center gap-2 bg-cream-2/60 rounded-full p-1">
                        <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <span className="w-6 text-center text-burgundy" data-testid={`cart-qty-${i.slug}`}>{i.quantity}</span>
                        <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="w-7 h-7 rounded-full bg-white flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                  </li>
                ))}
                <button onClick={clear} className="text-sm text-mahogany/60 hover:text-mahogany">Clear cart</button>
              </ul>

              <div className="p-6 rounded-3xl bg-white/80 border border-rose/40 sticky top-24" data-testid="cart-summary">
                <div className="flex items-center justify-between text-burgundy mb-4">
                  <span className="font-serif-display text-2xl">Summary</span>
                  <span className="text-xl font-medium" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="cart-name"
                  className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none mb-3" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for receipt *" data-testid="cart-email"
                  className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none mb-4" />
                <button onClick={checkout} disabled={loading} className="pill-btn w-full justify-center" data-testid="checkout-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Checkout · ${subtotal.toFixed(2)}</>}
                </button>
                <div className="mt-3 flex items-start gap-2 text-xs text-mahogany/70">
                  <Zap className="w-3.5 h-3.5 text-rose-italic mt-0.5 shrink-0" />
                  <span>{hasPhysical ? 'Shipping address' : 'Payment'} is collected securely by Stripe. Returning customers check out in one tap with <strong>Link</strong>, Apple&nbsp;Pay or Google&nbsp;Pay — no re-typing.</span>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
